/**
 * HC-RATING-004: ported from functions/src/utils/rating-eligibility.ts.
 * web-admin has no Application/OfferLetter model class — this is a plain
 * utility function operating on the raw offer_letters Firestore doc shape
 * (the same shape submitRating itself reads), mirroring the mobile Dart
 * port (mobile/lib/shared/utils/rating_eligibility.dart) for the web-admin
 * side.
 *
 * This is a UI-display-only check — it lets the employer UI show/hide the
 * "Rate" action without a network round-trip to submitRating first. It is
 * NOT the authoritative gate: submitRating always re-validates eligibility
 * server-side from the real offer_letters doc, and this client computation
 * must never be treated as a substitute for that check.
 *
 * Keep in sync with functions/src/utils/rating-eligibility.ts (and the Dart
 * port) if that file ever changes.
 */

export const RATING_AVAILABLE_DAYS_AFTER_END_DATE = 10;
export const RATING_AVAILABLE_DAYS_AFTER_START_DATE = 90;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Returns a parsed Date only for a genuine, parseable calendar date string.
 * Returns null for missing values, the "TBD" sentinel, and anything else
 * `new Date()` can't make sense of — callers must treat null as "no real
 * date available" rather than risk an Invalid Date leaking through.
 */
function parseRealDate(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed === 'TBD') return null;
  const parsed = new Date(trimmed);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * @param {{ startDate?: unknown, endDate?: unknown }} offerLetter
 * @param {Date} [now]
 * @returns {{ eligible: boolean, availableAt: Date|null, gateBasis: 'endDate'|'startDate' }}
 */
export function computeRatingEligibility(offerLetter, now = new Date()) {
  const realEndDate = parseRealDate(offerLetter?.endDate);

  if (realEndDate) {
    const availableAt = new Date(
      realEndDate.getTime() + RATING_AVAILABLE_DAYS_AFTER_END_DATE * MS_PER_DAY
    );
    return { eligible: now.getTime() >= availableAt.getTime(), availableAt, gateBasis: 'endDate' };
  }

  // No real endDate (missing, "TBD", or unparseable) — long-term/full-time
  // position, gate on startDate instead.
  const realStartDate = parseRealDate(offerLetter?.startDate);
  if (!realStartDate) {
    return { eligible: false, availableAt: null, gateBasis: 'startDate' };
  }
  const availableAt = new Date(
    realStartDate.getTime() + RATING_AVAILABLE_DAYS_AFTER_START_DATE * MS_PER_DAY
  );
  return { eligible: now.getTime() >= availableAt.getTime(), availableAt, gateBasis: 'startDate' };
}
